import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { Button } from "@/components/ui/button"
import {
  ArrowRight,
  BrainCircuit,
  Check,
  BarChart,
  Network,
  Cpu,
  LineChart,
} from "lucide-react"

import { Link } from "react-router-dom"

import { SEO } from "@/components/SEO"
import { PageFAQ } from "@/components/PageFAQ"
import { cognitiveStrategyFAQs } from "@/data/faqData"

export default function CognitiveStrategy() {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <SEO
        title="Cognitive Strategy | Blueprinting the Autonomous Enterprise | RSA Tech Group"
        description="Blueprinting the autonomous enterprise through predictive modeling and AI-driven decision frameworks. The brain of your enterprise."
        image="https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&q=80&w=2940"
      />
      <Navigation />

      {/* Hero Section */}
      <div className="relative pt-32 pb-24 bg-primary overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.1)_0%,transparent_70%)] opacity-30 pointer-events-none" />
        <div className="section-container relative z-10 text-center">
          <h1 className="text-4xl md:text-6xl font-black text-white tracking-tight mb-8">
            Cognitive <span className="text-accent">Strategy</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-10">
            Blueprinting the autonomous enterprise through predictive modeling
            and AI-driven decision frameworks.
          </p>
        </div>
      </div>

      {/* Content Section */}
      <section className="py-20 bg-gray-50">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
                The Brain of Your Enterprise
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Cognitive Strategy goes beyond traditional digital
                transformation. It's about embedding intelligence into the very
                core of your business logic. We help you transition from
                reactive systems to predictive, self-optimizing networks.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                By leveraging advanced machine learning and neural
                architectures, we create a roadmap that doesn't just digitize
                your processes, but makes them "aware" and adaptive to market
                changes.
              </p>

              <div className="grid grid-cols-1 gap-4 mb-8">
                {[
                  { icon: BrainCircuit, text: "AI Maturity Assessment" },
                  { icon: Network, text: "Neural Architecture Design" },
                  { icon: BarChart, text: "Predictive Capability Roadmap" },
                  { icon: Cpu, text: "Autonomous Agent Orchestration" },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-center text-primary font-medium p-4 bg-white rounded-lg shadow-sm border border-gray-100"
                  >
                    <div className="p-2 bg-accent/10 rounded-md mr-4">
                      <item.icon className="h-5 w-5 text-accent" />
                    </div>
                    {item.text}
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-accent/10 rounded-lg transform translate-x-4 translate-y-4" />
              <img
                src="https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&q=80&w=2940"
                alt="AI Neural Network Visualization"
                className="rounded-lg shadow-xl relative z-10 w-full object-cover h-[600px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Framework Section */}
      <CognitiveFrameworkSection />

      <Footer />
    </div>
  )
}

// Cognitive Framework Section Component
const frameworkSteps = [
  {
    id: "01",
    title: "Neural Discovery",
    description:
      "We map your existing data topography and identify high-value synaptic connections where AI can drive immediate impact. By analyzing your data flow, we uncover hidden patterns and opportunities for automation.",
    image:
      "https://images.unsplash.com/photo-1531403009284-440f080d1e12?auto=format&fit=crop&q=80&w=2940",
    alt: "Team collaborating on digital whiteboard",
  },
  {
    id: "02",
    title: "Cognitive Blueprinting",
    description:
      "Designing the neural architecture. We define the models, agents, and feedback loops that will power your enterprise. This phase ensures that every AI initiative is aligned with your core business objectives.",
    image:
      "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=2940",
    alt: " futuristic blueprints and data architecture",
  },
  {
    id: "03",
    title: "Generative Deployment",
    description:
      "Rolling out autonomous agents and LLMs into your workflow, ensuring seamless integration with legacy systems. We prioritize scalability and security, ensuring your new digital workforce operates reliably.",
    image:
      "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80&w=2940",
    alt: "Coding and deploying AI models",
  },
  {
    id: "04",
    title: "Evolutionary Optimization",
    description:
      "Implementing self-learning loops that allow your strategy to adapt and grow smarter over time. Your cognitive enterprise doesn't just run; it learns, evolves, and improves with every interaction.",
    image:
      "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=2940",
    alt: "Futuristic digital network",
  },
]

function CognitiveFrameworkSection() {
  return (
    <section className="py-24 bg-white">
      <div className="section-container">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <span className="text-accent font-semibold tracking-wider uppercase text-sm">
            How We Do It
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-primary mt-3 mb-6">
            The RSA Tech Group Cognitive Framework
          </h2>
          <p className="text-lg text-gray-600">
            A rigorous, science-backed methodology to transition your business
            from digital to intelligent.
          </p>
        </div>

        <div className="space-y-24">
          {frameworkSteps.map((step, index) => (
            <div
              key={step.id}
              className={`flex flex-col ${index % 2 === 1 ? "lg:flex-row-reverse" : "lg:flex-row"} gap-12 lg:gap-20 items-center`}
            >
              <div className="flex-1">
                <div className="relative">
                  <div
                    className={`absolute -inset-4 ${index % 2 === 1 ? "bg-primary/5" : "bg-accent/5"} rounded-2xl transform rotate-3 transition-transform duration-500 hover:rotate-0`}
                  />
                  <img
                    src={step.image}
                    alt={step.alt}
                    className="relative rounded-xl shadow-2xl w-full object-cover h-[400px] hover:scale-[1.02] transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-md px-4 py-2 rounded-lg shadow-lg font-black text-xl text-primary border border-gray-100">
                    PHASE {step.id}
                  </div>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-3xl font-bold text-primary mb-6">
                  {step.title}
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed mb-8">
                  {step.description}
                </p>
                <ul className="space-y-3">
                  {[
                    "Strategic Alignment",
                    "Technical Validation",
                    "Scalable Architecture",
                  ].map((item, i) => (
                    <li
                      key={i}
                      className="flex items-center text-gray-700 font-medium"
                    >
                      <div className="h-2 w-2 rounded-full bg-accent mr-3" />
                      {item} for {step.title}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-20 text-center">
        <Link to="/contact">
          <Button
            size="xl"
            className="bg-primary hover:bg-primary/90 text-white font-bold px-10 py-6 text-lg shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
          >
            Start Your Transformation <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </Link>
      </div>

      <PageFAQ
        faqs={cognitiveStrategyFAQs}
        title="Cognitive Strategy FAQs"
        subtitle="Common questions about AI strategy and enterprise transformation"
        variant="muted"
      />
    </section>
  )
}
